import { TFunction } from 'i18next'
import { Chip } from 'vnetwork-ui'
import { formatCurrency } from '@/utils/function'
import {
    DataTableColumn,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { TenderBidResult } from '../../types'

type Color =
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'warning'
    | 'success'

const getBidResultStatus = (
    bidResult: number | null,
    t: TFunction
): { label: string; color: Color } => {
    if (bidResult === null || bidResult === undefined) {
        return { label: t('bid_result.status.no_result'), color: 'default' }
    }
    if (bidResult === 1) {
        return { label: t('bid_result.status.won'), color: 'success' }
    }
    if (bidResult === 0) {
        return { label: t('bid_result.status.lost'), color: 'error' }
    }
    return {
        label: t('bid_result.status.other', { value: bidResult }),
        color: 'warning',
    }
}

export const getBidResultColumns = (
    t: TFunction
): DataTableColumn<TenderBidResult>[] => [
    {
        key: 'org_fullname',
        title: t('bid_result.org_fullname'),
        width: 500,
        render: (value) => (
            <span className="text-sm truncate block">{value || '-'}</span>
        ),
    },
    {
        key: 'taxcode',
        title: t('bid_result.taxcode'),
        width: 100,
        render: (value) => <span className="text-sm">{value || '-'}</span>,
    },
    {
        key: 'lot_final_price',
        title: t('bid_result.lot_final_price'),
        width: 180,
        render: (value) => (
            <span className="text-sm text-right block">
                {formatCurrency(value || 0, 'VND')}
            </span>
        ),
    },
    {
        key: 'bid_winning_price',
        title: t('bid_result.bid_winning_price'),
        width: 180,
        render: (value) => (
            <span className="text-sm text-right block">
                {formatCurrency(value || 0, 'VND')}
            </span>
        ),
    },
    {
        key: 'bid_result',
        title: t('bid_result.bid_result'),
        width: 150,
        render: (_value, row) => {
            const status = getBidResultStatus(row.bid_result, t)
            return (
                <Chip
                    label={status.label}
                    size="small"
                    variant="contained"
                    color={status.color}
                    className="px-5"
                />
            )
        },
    },
    {
        key: 'reason',
        title: t('bid_result.reason'),
        width: 200,
        render: (value) => (
            <span className="text-sm truncate block">{value || '-'}</span>
        ),
    },
    {
        key: 'bid_execution_time',
        title: t('bid_result.bid_execution_time'),
        width: 180,
        render: (value) => <span className="text-sm">{value || '-'}</span>,
    },
    {
        key: 'cperiod_text',
        title: t('bid_result.cperiod_text'),
        width: 180,
        render: (value) => <span className="text-sm">{value || '-'}</span>,
    },
]

export const getBidResultFilterToggles = (t: TFunction): FilterToggle[] => [
    { key: 'org_fullname', label: t('bid_result.org_fullname') },
    { key: 'taxcode', label: t('bid_result.taxcode') },
    { key: 'lot_final_price', label: t('bid_result.lot_final_price') },
    { key: 'bid_winning_price', label: t('bid_result.bid_winning_price') },
    { key: 'bid_result', label: t('bid_result.bid_result') },
    { key: 'reason', label: t('bid_result.reason') },
    { key: 'bid_execution_time', label: t('bid_result.bid_execution_time') },
    { key: 'cperiod_text', label: t('bid_result.cperiod_text') },
]
